/**
 * French -> Korean core vocabulary: the most frequent, simplest words of
 * everyday spoken Korean (TOPIK I territory), for a French-speaking beginner.
 *
 * The learner is shown `fr` and types the Korean word. `ko` is the main
 * answer, in Hangul, dictionary form for verbs and adjectives; `alt` lists
 * other Korean forms accepted as correct (synonyms of the same register,
 * loanword/native pairs, the polite form where that is what is actually
 * said). Romanised spellings are NOT stored here: shared/korean.ts derives
 * them from the Hangul with shared/romanize.ts, so a learner without a
 * Korean keyboard can answer in Latin letters.
 *
 * `fr` is the progress key, so it is unique. `ko` is not: Korean has real
 * homographs (눈 eye / snow, 배 stomach / boat, 쓰다 write / bitter) and
 * each sense is its own card, told apart by the French prompt.
 *
 * See ./README.md for curation rules and counts.
 */

export type KoreanTopic =
  | "greetings" | "pronouns" | "numbers" | "time" | "family"
  | "body-health" | "food" | "home" | "clothes-colours" | "city-transport"
  | "nature-weather" | "school-work" | "shopping-money"
  | "verbs-basic" | "verbs-daily" | "verbs-move" | "verbs-social"
  | "adjectives" | "adverbs" | "hobbies-media" | "tech";

export type KoreanPos = "n" | "v" | "adj" | "adv" | "expr" | "num" | "pron";

export type KoreanEntry = {
  fr: string;        // the prompt shown to the learner
  ko: string;        // the main answer, Hangul
  alt: string[];     // other accepted Korean forms (may be empty)
  topic: KoreanTopic;
  pos: KoreanPos;
};

// One literal per topic: a single 1 000-element literal is too big for tsc (TS2590).
const T_GREETINGS: KoreanEntry[] = [ // greetings
  { fr: "bonjour (formule polie)", ko: "안녕하세요", alt: ["안녕하십니까"], topic: "greetings", pos: "expr" },
  { fr: "salut (familier, entre amis)", ko: "안녕", alt: [], topic: "greetings", pos: "expr" },
  { fr: "merci", ko: "감사합니다", alt: ["고맙습니다", "고마워요", "고마워"], topic: "greetings", pos: "expr" },
  { fr: "je suis désolé (formel)", ko: "죄송합니다", alt: ["죄송해요"], topic: "greetings", pos: "expr" },
  { fr: "désolé / pardon (moins formel)", ko: "미안해요", alt: ["미안합니다", "미안해"], topic: "greetings", pos: "expr" },
  { fr: "ça va / ce n'est pas grave", ko: "괜찮아요", alt: ["괜찮습니다"], topic: "greetings", pos: "expr" },
  { fr: "oui", ko: "네", alt: ["예"], topic: "greetings", pos: "expr" },
  { fr: "non", ko: "아니요", alt: ["아뇨"], topic: "greetings", pos: "expr" },
  { fr: "un instant, s'il vous plaît", ko: "잠깐만요", alt: ["잠시만요"], topic: "greetings", pos: "expr" },
  { fr: "c'est combien ?", ko: "얼마예요", alt: ["얼마입니까"], topic: "greetings", pos: "expr" },
  { fr: "au revoir (à celui qui part)", ko: "안녕히 가세요", alt: [], topic: "greetings", pos: "expr" },
  { fr: "au revoir (à celui qui reste)", ko: "안녕히 계세요", alt: [], topic: "greetings", pos: "expr" },
  { fr: "enchanté (de vous rencontrer)", ko: "반갑습니다", alt: ["만나서 반갑습니다", "만나서 반가워요", "반가워요"], topic: "greetings", pos: "expr" },
  { fr: "donnez-moi..., s'il vous plaît", ko: "주세요", alt: [], topic: "greetings", pos: "expr" },
  { fr: "de rien / il n'y a pas de quoi", ko: "아니에요", alt: ["천만에요", "별말씀을요"], topic: "greetings", pos: "expr" },
  { fr: "bienvenue (entrez, dans un magasin)", ko: "어서 오세요", alt: ["환영합니다"], topic: "greetings", pos: "expr" },
  { fr: "bon appétit (dit par celui qui va manger)", ko: "잘 먹겠습니다", alt: [], topic: "greetings", pos: "expr" },
  { fr: "merci pour le repas (après avoir mangé)", ko: "잘 먹었습니다", alt: [], topic: "greetings", pos: "expr" },
  { fr: "bon appétit (dit à celui qui mange)", ko: "맛있게 드세요", alt: [], topic: "greetings", pos: "expr" },
  { fr: "bonne nuit", ko: "안녕히 주무세요", alt: ["잘 자요", "잘 자"], topic: "greetings", pos: "expr" },
  { fr: "à la prochaine / à bientôt", ko: "또 봐요", alt: ["다음에 봐요", "나중에 봐요", "또 만나요"], topic: "greetings", pos: "expr" },
  { fr: "félicitations", ko: "축하합니다", alt: ["축하해요"], topic: "greetings", pos: "expr" },
  { fr: "joyeux anniversaire", ko: "생일 축하합니다", alt: ["생일 축하해요", "생일 축하해"], topic: "greetings", pos: "expr" },
  { fr: "je ne sais pas", ko: "몰라요", alt: ["모르겠어요"], topic: "greetings", pos: "expr" },
  { fr: "compris / d'accord, j'ai compris", ko: "알겠어요", alt: ["알겠습니다", "알았어요"], topic: "greetings", pos: "expr" },
  { fr: "je ne comprends pas", ko: "이해가 안 돼요", alt: ["이해가 안 가요", "이해 못 해요", "못 알아들었어요"], topic: "greetings", pos: "expr" },
  { fr: "excusez-moi (pour appeler quelqu'un, un serveur)", ko: "저기요", alt: ["여기요"], topic: "greetings", pos: "expr" },
  { fr: "excusez-moi (formel, en dérangeant)", ko: "실례합니다", alt: ["실례해요"], topic: "greetings", pos: "expr" },
  { fr: "vraiment ?", ko: "정말요", alt: ["진짜요", "정말이에요"], topic: "greetings", pos: "expr" },
  { fr: "bon voyage / à tout à l'heure (à celui qui sort et revient)", ko: "잘 다녀오세요", alt: ["잘 다녀와요"], topic: "greetings", pos: "expr" },
  { fr: "je suis rentré (en arrivant à la maison)", ko: "다녀왔습니다", alt: ["다녀왔어요"], topic: "greetings", pos: "expr" },
  { fr: "attention ! / faites attention", ko: "조심하세요", alt: ["조심해요"], topic: "greetings", pos: "expr" },
  { fr: "comment allez-vous ?", ko: "잘 지내세요", alt: ["어떻게 지내세요", "잘 지냈어요", "잘 지내요"], topic: "greetings", pos: "expr" },
  { fr: "bon courage / courage !", ko: "힘내세요", alt: ["힘내요", "파이팅", "화이팅"], topic: "greetings", pos: "expr" },
  { fr: "répétez, s'il vous plaît", ko: "다시 한번 말해 주세요", alt: ["다시 한 번 말해 주세요", "다시 말해 주세요"], topic: "greetings", pos: "expr" },
  { fr: "parlez plus lentement, s'il vous plaît", ko: "천천히 말해 주세요", alt: [], topic: "greetings", pos: "expr" },
  { fr: "comment dit-on ... en coréen ?", ko: "한국어로 뭐예요", alt: ["한국말로 뭐예요", "한국어로 뭐라고 해요"], topic: "greetings", pos: "expr" },
  { fr: "où sont les toilettes ?", ko: "화장실이 어디예요", alt: ["화장실 어디예요"], topic: "greetings", pos: "expr" },
  { fr: "l'addition, s'il vous plaît", ko: "계산해 주세요", alt: ["계산이요"], topic: "greetings", pos: "expr" },
  { fr: "aidez-moi ! / au secours", ko: "도와주세요", alt: ["살려 주세요"], topic: "greetings", pos: "expr" },
  { fr: "bonne journée", ko: "좋은 하루 보내세요", alt: ["좋은 하루 되세요"], topic: "greetings", pos: "expr" },
  { fr: "santé ! (en trinquant)", ko: "건배", alt: [], topic: "greetings", pos: "expr" },
  { fr: "comment vous appelez-vous ?", ko: "이름이 뭐예요", alt: ["성함이 어떻게 되세요"], topic: "greetings", pos: "expr" },
]; const T_PRONOUNS: KoreanEntry[] = [ // pronouns
  { fr: "je, moi (poli, humble)", ko: "저", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "je, moi (familier)", ko: "나", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "nous (poli, humble)", ko: "저희", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "nous / notre", ko: "우리", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "tu, toi (familier)", ko: "너", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "vous (formel, rare à l'oral ; aussi entre époux)", ko: "당신", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "il, lui", ko: "그", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "elle", ko: "그녀", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "ils, eux", ko: "그들", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "vous tous, mesdames et messieurs", ko: "여러분", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "mon, ma (poli)", ko: "제", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "mon, ma (familier)", ko: "내", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "ceci, celui-ci (près de moi)", ko: "이것", alt: ["이거"], topic: "pronouns", pos: "pron" },
  { fr: "cela, celui-là (près de toi)", ko: "그것", alt: ["그거"], topic: "pronouns", pos: "pron" },
  { fr: "cela là-bas, celui-là (loin de nous deux)", ko: "저것", alt: ["저거"], topic: "pronouns", pos: "pron" },
  { fr: "ici", ko: "여기", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "là (près de toi)", ko: "거기", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "là-bas", ko: "저기", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "par ici, de ce côté-ci", ko: "이쪽", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "de ce côté-là (près de toi)", ko: "그쪽", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "là-bas, de ce côté-là (loin)", ko: "저쪽", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "comme ceci, de cette façon-ci", ko: "이렇게", alt: [], topic: "pronouns", pos: "adv" },
  { fr: "comme cela, de cette façon-là", ko: "그렇게", alt: [], topic: "pronouns", pos: "adv" },
  { fr: "qui", ko: "누구", alt: ["누가"], topic: "pronouns", pos: "pron" },
  { fr: "quoi, que", ko: "뭐", alt: ["무엇"], topic: "pronouns", pos: "pron" },
  { fr: "où", ko: "어디", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "quand", ko: "언제", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "pourquoi", ko: "왜", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "comment", ko: "어떻게", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "combien (degré, quantité : à quel point)", ko: "얼마나", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "combien de (suivi d'un classificateur)", ko: "몇", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "quel, lequel (parmi plusieurs)", ko: "어느", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "quel, quel genre de", ko: "무슨", alt: [], topic: "pronouns", pos: "pron" },
  { fr: "la chose / le fait (nominalisateur)", ko: "것", alt: ["거"], topic: "pronouns", pos: "n" },
  { fr: "la personne", ko: "사람", alt: [], topic: "pronouns", pos: "n" },
  { fr: "cette personne-ci (près de moi)", ko: "이 사람", alt: [], topic: "pronouns", pos: "n" },
  { fr: "cette personne-là (là-bas)", ko: "저 사람", alt: [], topic: "pronouns", pos: "n" },
  { fr: "cette personne-là / il, elle (honorifique)", ko: "그분", alt: [], topic: "pronouns", pos: "n" },
  { fr: "tout le monde / tous", ko: "모두", alt: ["다"], topic: "pronouns", pos: "pron" },
  { fr: "quelque chose", ko: "뭔가", alt: ["무언가"], topic: "pronouns", pos: "pron" },
]; const T_NUMBERS: KoreanEntry[] = [ // numbers
  { fr: "un (nombre natif, en comptant)", ko: "하나", alt: ["한"], topic: "numbers", pos: "num" },
  { fr: "deux (nombre natif)", ko: "둘", alt: ["두"], topic: "numbers", pos: "num" },
  { fr: "trois (nombre natif)", ko: "셋", alt: ["세"], topic: "numbers", pos: "num" },
  { fr: "quatre (nombre natif)", ko: "넷", alt: ["네"], topic: "numbers", pos: "num" },
  { fr: "cinq (nombre natif)", ko: "다섯", alt: [], topic: "numbers", pos: "num" },
  { fr: "six (nombre natif)", ko: "여섯", alt: [], topic: "numbers", pos: "num" },
  { fr: "sept (nombre natif)", ko: "일곱", alt: [], topic: "numbers", pos: "num" },
  { fr: "huit (nombre natif)", ko: "여덟", alt: [], topic: "numbers", pos: "num" },
  { fr: "neuf (nombre natif)", ko: "아홉", alt: [], topic: "numbers", pos: "num" },
  { fr: "dix (nombre natif)", ko: "열", alt: [], topic: "numbers", pos: "num" },
  { fr: "vingt (nombre natif)", ko: "스물", alt: ["스무"], topic: "numbers", pos: "num" },
  { fr: "trente (nombre natif)", ko: "서른", alt: [], topic: "numbers", pos: "num" },
  { fr: "quarante (nombre natif)", ko: "마흔", alt: [], topic: "numbers", pos: "num" },
  { fr: "cinquante (nombre natif)", ko: "쉰", alt: [], topic: "numbers", pos: "num" },
  { fr: "zéro", ko: "영", alt: ["공"], topic: "numbers", pos: "num" },
  { fr: "un (nombre sino-coréen)", ko: "일", alt: [], topic: "numbers", pos: "num" },
  { fr: "deux (nombre sino-coréen)", ko: "이", alt: [], topic: "numbers", pos: "num" },
  { fr: "trois (nombre sino-coréen)", ko: "삼", alt: [], topic: "numbers", pos: "num" },
  { fr: "quatre (nombre sino-coréen)", ko: "사", alt: [], topic: "numbers", pos: "num" },
  { fr: "cinq (nombre sino-coréen)", ko: "오", alt: [], topic: "numbers", pos: "num" },
  { fr: "six (nombre sino-coréen)", ko: "육", alt: [], topic: "numbers", pos: "num" },
  { fr: "sept (nombre sino-coréen)", ko: "칠", alt: [], topic: "numbers", pos: "num" },
  { fr: "huit (nombre sino-coréen)", ko: "팔", alt: [], topic: "numbers", pos: "num" },
  { fr: "neuf (nombre sino-coréen)", ko: "구", alt: [], topic: "numbers", pos: "num" },
  { fr: "dix (nombre sino-coréen)", ko: "십", alt: [], topic: "numbers", pos: "num" },
  { fr: "cent", ko: "백", alt: [], topic: "numbers", pos: "num" },
  { fr: "mille", ko: "천", alt: [], topic: "numbers", pos: "num" },
  { fr: "dix mille", ko: "만", alt: [], topic: "numbers", pos: "num" },
  { fr: "... objet(s) (classificateur général)", ko: "개", alt: [], topic: "numbers", pos: "n" },
  { fr: "... personne(s) (classificateur)", ko: "명", alt: [], topic: "numbers", pos: "n" },
  { fr: "... animal, animaux (classificateur)", ko: "마리", alt: [], topic: "numbers", pos: "n" },
  { fr: "... an(s) (âge, avec les nombres natifs)", ko: "살", alt: [], topic: "numbers", pos: "n" },
  { fr: "... fois (classificateur) / numéro", ko: "번", alt: [], topic: "numbers", pos: "n" },
  { fr: "... heure(s) (heure de l'horloge, avec les nombres natifs)", ko: "시", alt: [], topic: "numbers", pos: "n" },
  { fr: "le won (monnaie coréenne)", ko: "원", alt: [], topic: "numbers", pos: "n" },
  { fr: "... étage(s)", ko: "층", alt: [], topic: "numbers", pos: "n" },
  { fr: "... livre(s), volume(s) (classificateur)", ko: "권", alt: [], topic: "numbers", pos: "n" },
  { fr: "... verre(s), tasse(s) (classificateur)", ko: "잔", alt: [], topic: "numbers", pos: "n" },
  { fr: "... bouteille(s) (classificateur)", ko: "병", alt: [], topic: "numbers", pos: "n" },
  { fr: "... feuille(s), billet(s) (classificateur des objets plats)", ko: "장", alt: [], topic: "numbers", pos: "n" },
  { fr: "demi, la moitié", ko: "반", alt: [], topic: "numbers", pos: "n" },
  { fr: "premier (le premier, la première fois)", ko: "첫 번째", alt: ["첫째"], topic: "numbers", pos: "num" },
  { fr: "deuxième", ko: "둘째", alt: ["두 번째"], topic: "numbers", pos: "num" },
  { fr: "plusieurs", ko: "여러", alt: [], topic: "numbers", pos: "pron" },
  { fr: "tous les, tout (déterminant)", ko: "모든", alt: [], topic: "numbers", pos: "pron" },
  { fr: "combien (d'objets, de choses)", ko: "몇 개", alt: [], topic: "numbers", pos: "num" },
  { fr: "le nombre, le chiffre", ko: "숫자", alt: [], topic: "numbers", pos: "n" },
  { fr: "le numéro (de téléphone, de salle)", ko: "번호", alt: [], topic: "numbers", pos: "n" },
]; const T_TIME: KoreanEntry[] = [ // time
  { fr: "lundi", ko: "월요일", alt: [], topic: "time", pos: "n" },
  { fr: "mardi", ko: "화요일", alt: [], topic: "time", pos: "n" },
  { fr: "mercredi", ko: "수요일", alt: [], topic: "time", pos: "n" },
  { fr: "jeudi", ko: "목요일", alt: [], topic: "time", pos: "n" },
  { fr: "vendredi", ko: "금요일", alt: [], topic: "time", pos: "n" },
  { fr: "samedi", ko: "토요일", alt: [], topic: "time", pos: "n" },
  { fr: "dimanche", ko: "일요일", alt: [], topic: "time", pos: "n" },
  { fr: "aujourd'hui", ko: "오늘", alt: [], topic: "time", pos: "n" },
  { fr: "demain", ko: "내일", alt: [], topic: "time", pos: "n" },
  { fr: "hier", ko: "어제", alt: [], topic: "time", pos: "n" },
  { fr: "après-demain", ko: "모레", alt: [], topic: "time", pos: "n" },
  { fr: "avant-hier", ko: "그저께", alt: ["그제"], topic: "time", pos: "n" },
  { fr: "le matin", ko: "아침", alt: [], topic: "time", pos: "n" },
  { fr: "la matinée / le matin (avant midi, AM)", ko: "오전", alt: [], topic: "time", pos: "n" },
  { fr: "l'après-midi (PM)", ko: "오후", alt: [], topic: "time", pos: "n" },
  { fr: "le soir", ko: "저녁", alt: [], topic: "time", pos: "n" },
  { fr: "la nuit", ko: "밤", alt: [], topic: "time", pos: "n" },
  { fr: "la journée (le jour, opposé à la nuit)", ko: "낮", alt: [], topic: "time", pos: "n" },
  { fr: "l'aube / très tôt le matin", ko: "새벽", alt: [], topic: "time", pos: "n" },
  { fr: "l'heure (durée, 60 minutes) / le temps", ko: "시간", alt: [], topic: "time", pos: "n" },
  { fr: "la minute", ko: "분", alt: [], topic: "time", pos: "n" },
  { fr: "la seconde", ko: "초", alt: [], topic: "time", pos: "n" },
  { fr: "la semaine", ko: "주", alt: ["주일", "일주일"], topic: "time", pos: "n" },
  { fr: "le mois (durée)", ko: "달", alt: ["개월"], topic: "time", pos: "n" },
  { fr: "l'année", ko: "년", alt: ["해"], topic: "time", pos: "n" },
  { fr: "le jour", ko: "날", alt: [], topic: "time", pos: "n" },
  { fr: "une journée, un jour (entier)", ko: "하루", alt: [], topic: "time", pos: "n" },
  { fr: "la date", ko: "날짜", alt: [], topic: "time", pos: "n" },
  { fr: "le moment / quand (au moment où)", ko: "때", alt: [], topic: "time", pos: "n" },
  { fr: "... mois (dans les dates, avec nombre sino-coréen)", ko: "월", alt: [], topic: "time", pos: "n" },
  { fr: "janvier", ko: "일월", alt: ["1월"], topic: "time", pos: "n" },
  { fr: "décembre", ko: "십이월", alt: ["12월"], topic: "time", pos: "n" },
  { fr: "maintenant", ko: "지금", alt: [], topic: "time", pos: "adv" },
  { fr: "plus tard", ko: "나중에", alt: [], topic: "time", pos: "adv" },
  { fr: "tout à l'heure (dans un moment)", ko: "이따가", alt: [], topic: "time", pos: "adv" },
  { fr: "à l'instant, il y a un moment", ko: "방금", alt: ["금방"], topic: "time", pos: "adv" },
  { fr: "avant (après un nom : deux heures avant)", ko: "전", alt: ["전에"], topic: "time", pos: "n" },
  { fr: "après (après un nom : deux heures après)", ko: "후", alt: ["후에"], topic: "time", pos: "n" },
  { fr: "d'abord, en premier", ko: "먼저", alt: [], topic: "time", pos: "adv" },
  { fr: "tôt", ko: "일찍", alt: [], topic: "time", pos: "adv" },
  { fr: "tard, en retard", ko: "늦게", alt: [], topic: "time", pos: "adv" },
  { fr: "bientôt", ko: "곧", alt: [], topic: "time", pos: "adv" },
  { fr: "déjà", ko: "벌써", alt: ["이미"], topic: "time", pos: "adv" },
  { fr: "pas encore / encore (toujours)", ko: "아직", alt: [], topic: "time", pos: "adv" },
  { fr: "ces jours-ci, en ce moment", ko: "요즘", alt: ["요새"], topic: "time", pos: "adv" },
  { fr: "le printemps", ko: "봄", alt: [], topic: "time", pos: "n" },
  { fr: "l'été", ko: "여름", alt: [], topic: "time", pos: "n" },
  { fr: "l'automne", ko: "가을", alt: [], topic: "time", pos: "n" },
  { fr: "l'hiver", ko: "겨울", alt: [], topic: "time", pos: "n" },
  { fr: "la saison", ko: "계절", alt: [], topic: "time", pos: "n" },
  { fr: "le week-end", ko: "주말", alt: [], topic: "time", pos: "n" },
  { fr: "le jour de semaine (jour ouvrable)", ko: "평일", alt: [], topic: "time", pos: "n" },
  { fr: "chaque jour, tous les jours", ko: "매일", alt: ["날마다"], topic: "time", pos: "adv" },
  { fr: "toujours", ko: "항상", alt: ["늘", "언제나"], topic: "time", pos: "adv" },
  { fr: "souvent", ko: "자주", alt: [], topic: "time", pos: "adv" },
  { fr: "parfois, de temps en temps", ko: "가끔", alt: ["때때로"], topic: "time", pos: "adv" },
  { fr: "la première fois / le début", ko: "처음", alt: [], topic: "time", pos: "n" },
  { fr: "le dernier, la dernière (la dernière fois)", ko: "마지막", alt: [], topic: "time", pos: "n" },
  { fr: "la montre / l'horloge", ko: "시계", alt: ["손목시계"], topic: "time", pos: "n" },
  { fr: "le calendrier", ko: "달력", alt: [], topic: "time", pos: "n" },
  { fr: "l'anniversaire", ko: "생일", alt: [], topic: "time", pos: "n" },
  { fr: "les vacances scolaires", ko: "방학", alt: [], topic: "time", pos: "n" },
  { fr: "les vacances / les congés", ko: "휴가", alt: [], topic: "time", pos: "n" },
  { fr: "cette fois-ci / ce, cette (cette semaine, ce mois-ci)", ko: "이번", alt: [], topic: "time", pos: "n" },
  { fr: "dernier, passé (la semaine dernière)", ko: "지난", alt: [], topic: "time", pos: "n" },
  { fr: "prochain, suivant", ko: "다음", alt: [], topic: "time", pos: "n" },
]; const T_FAMILY: KoreanEntry[] = [ // family
  { fr: "la famille", ko: "가족", alt: [], topic: "family", pos: "n" },
  { fr: "les parents (père et mère)", ko: "부모님", alt: ["부모"], topic: "family", pos: "n" },
  { fr: "le père", ko: "아버지", alt: ["아빠"], topic: "family", pos: "n" },
  { fr: "la mère", ko: "어머니", alt: ["엄마"], topic: "family", pos: "n" },
  { fr: "le frère aîné (d'un homme)", ko: "형", alt: [], topic: "family", pos: "n" },
  { fr: "le frère aîné (d'une femme)", ko: "오빠", alt: [], topic: "family", pos: "n" },
  { fr: "la sœur aînée (d'un homme)", ko: "누나", alt: [], topic: "family", pos: "n" },
  { fr: "la sœur aînée (d'une femme)", ko: "언니", alt: [], topic: "family", pos: "n" },
  { fr: "le frère cadet (petit frère)", ko: "남동생", alt: [], topic: "family", pos: "n" },
  { fr: "la sœur cadette (petite sœur)", ko: "여동생", alt: [], topic: "family", pos: "n" },
  { fr: "le cadet / la cadette (frère ou sœur plus jeune)", ko: "동생", alt: [], topic: "family", pos: "n" },
  { fr: "le grand-père", ko: "할아버지", alt: [], topic: "family", pos: "n" },
  { fr: "la grand-mère", ko: "할머니", alt: [], topic: "family", pos: "n" },
  { fr: "le mari", ko: "남편", alt: [], topic: "family", pos: "n" },
  { fr: "la femme (épouse)", ko: "아내", alt: ["와이프", "집사람"], topic: "family", pos: "n" },
  { fr: "le fils", ko: "아들", alt: [], topic: "family", pos: "n" },
  { fr: "la fille (de ses parents)", ko: "딸", alt: [], topic: "family", pos: "n" },
  { fr: "le bébé", ko: "아기", alt: ["애기"], topic: "family", pos: "n" },
  { fr: "l'enfant", ko: "아이", alt: ["애", "어린이"], topic: "family", pos: "n" },
  { fr: "l'ami, l'amie", ko: "친구", alt: [], topic: "family", pos: "n" },
  { fr: "le voisin, la voisine", ko: "이웃", alt: [], topic: "family", pos: "n" },
  { fr: "le client / l'invité", ko: "손님", alt: ["고객"], topic: "family", pos: "n" },
  { fr: "l'oncle (frère du père)", ko: "삼촌", alt: [], topic: "family", pos: "n" },
  { fr: "la tante (sœur de la mère)", ko: "이모", alt: [], topic: "family", pos: "n" },
  { fr: "le cousin, la cousine", ko: "사촌", alt: [], topic: "family", pos: "n" },
  { fr: "l'homme (personne de sexe masculin)", ko: "남자", alt: [], topic: "family", pos: "n" },
  { fr: "la femme (personne de sexe féminin)", ko: "여자", alt: [], topic: "family", pos: "n" },
]; const T_BODY_HEALTH: KoreanEntry[] = [ // body-health
  { fr: "le corps", ko: "몸", alt: [], topic: "body-health", pos: "n" },
  { fr: "la tête", ko: "머리", alt: [], topic: "body-health", pos: "n" },
  { fr: "les cheveux", ko: "머리카락", alt: ["머리"], topic: "body-health", pos: "n" },
  { fr: "le visage", ko: "얼굴", alt: [], topic: "body-health", pos: "n" },
  { fr: "l'œil", ko: "눈", alt: [], topic: "body-health", pos: "n" },
  { fr: "le nez", ko: "코", alt: [], topic: "body-health", pos: "n" },
  { fr: "la bouche", ko: "입", alt: [], topic: "body-health", pos: "n" },
  { fr: "l'oreille", ko: "귀", alt: [], topic: "body-health", pos: "n" },
  { fr: "la main", ko: "손", alt: [], topic: "body-health", pos: "n" },
  { fr: "le pied", ko: "발", alt: [], topic: "body-health", pos: "n" },
  { fr: "la jambe", ko: "다리", alt: [], topic: "body-health", pos: "n" },
  { fr: "le bras", ko: "팔", alt: [], topic: "body-health", pos: "n" },
  { fr: "le ventre", ko: "배", alt: [], topic: "body-health", pos: "n" },
  { fr: "le dos", ko: "등", alt: [], topic: "body-health", pos: "n" },
  { fr: "le cou", ko: "목", alt: [], topic: "body-health", pos: "n" },
  { fr: "l'épaule", ko: "어깨", alt: [], topic: "body-health", pos: "n" },
  { fr: "le genou", ko: "무릎", alt: [], topic: "body-health", pos: "n" },
  { fr: "le sang", ko: "피", alt: [], topic: "body-health", pos: "n" },
  { fr: "le cœur (sentiment)", ko: "마음", alt: [], topic: "body-health", pos: "n" },
  { fr: "le cœur (organe)", ko: "심장", alt: [], topic: "body-health", pos: "n" },
  { fr: "la taille (hauteur)", ko: "키", alt: [], topic: "body-health", pos: "n" },
  { fr: "le poids (du corps)", ko: "몸무게", alt: ["체중"], topic: "body-health", pos: "n" },
  { fr: "la santé", ko: "건강", alt: [], topic: "body-health", pos: "n" },
  { fr: "la maladie", ko: "병", alt: [], topic: "body-health", pos: "n" },
  { fr: "le rhume", ko: "감기", alt: [], topic: "body-health", pos: "n" },
  { fr: "la fièvre", ko: "열", alt: [], topic: "body-health", pos: "n" },
  { fr: "le médicament", ko: "약", alt: [], topic: "body-health", pos: "n" },
  { fr: "l'hôpital", ko: "병원", alt: [], topic: "body-health", pos: "n" },
  { fr: "le médecin", ko: "의사", alt: [], topic: "body-health", pos: "n" },
  { fr: "l'infirmier / l'infirmière", ko: "간호사", alt: [], topic: "body-health", pos: "n" },
  { fr: "la pharmacie", ko: "약국", alt: [], topic: "body-health", pos: "n" },
  { fr: "la dent", ko: "이", alt: ["치아", "이빨"], topic: "body-health", pos: "n" },
  { fr: "le doigt", ko: "손가락", alt: [], topic: "body-health", pos: "n" },
  { fr: "l'orteil", ko: "발가락", alt: [], topic: "body-health", pos: "n" },
  { fr: "la peau", ko: "피부", alt: [], topic: "body-health", pos: "n" },
  { fr: "l'os", ko: "뼈", alt: [], topic: "body-health", pos: "n" },
  { fr: "le mal de tête", ko: "두통", alt: [], topic: "body-health", pos: "n" },
  { fr: "la toux", ko: "기침", alt: [], topic: "body-health", pos: "n" },
  { fr: "le stress", ko: "스트레스", alt: [], topic: "body-health", pos: "n" },
  { fr: "l'exercice physique / le sport", ko: "운동", alt: [], topic: "body-health", pos: "n" },
  { fr: "la lèvre", ko: "입술", alt: [], topic: "body-health", pos: "n" },
  { fr: "la langue (organe)", ko: "혀", alt: [], topic: "body-health", pos: "n" },
  { fr: "le front", ko: "이마", alt: [], topic: "body-health", pos: "n" },
  { fr: "la blessure", ko: "상처", alt: [], topic: "body-health", pos: "n" },
  { fr: "le sommeil", ko: "잠", alt: [], topic: "body-health", pos: "n" },
]; const T_FOOD: KoreanEntry[] = [ // food
  { fr: "le riz (cuit)", ko: "밥", alt: [], topic: "food", pos: "n" },
  { fr: "le riz (cru)", ko: "쌀", alt: [], topic: "food", pos: "n" },
  { fr: "la soupe", ko: "국", alt: ["수프", "스프"], topic: "food", pos: "n" },
  { fr: "le kimchi", ko: "김치", alt: [], topic: "food", pos: "n" },
  { fr: "les nouilles instantanées", ko: "라면", alt: [], topic: "food", pos: "n" },
  { fr: "le pain", ko: "빵", alt: [], topic: "food", pos: "n" },
  { fr: "la viande", ko: "고기", alt: [], topic: "food", pos: "n" },
  { fr: "le bœuf", ko: "소고기", alt: ["쇠고기"], topic: "food", pos: "n" },
  { fr: "le porc", ko: "돼지고기", alt: [], topic: "food", pos: "n" },
  { fr: "le poulet (viande)", ko: "닭고기", alt: [], topic: "food", pos: "n" },
  { fr: "le poulet frit", ko: "치킨", alt: [], topic: "food", pos: "n" },
  { fr: "le poisson (aliment)", ko: "생선", alt: [], topic: "food", pos: "n" },
  { fr: "l'œuf", ko: "계란", alt: ["달걀"], topic: "food", pos: "n" },
  { fr: "les légumes", ko: "채소", alt: ["야채"], topic: "food", pos: "n" },
  { fr: "les fruits", ko: "과일", alt: [], topic: "food", pos: "n" },
  { fr: "la pomme", ko: "사과", alt: [], topic: "food", pos: "n" },
  { fr: "la banane", ko: "바나나", alt: [], topic: "food", pos: "n" },
  { fr: "la fraise", ko: "딸기", alt: [], topic: "food", pos: "n" },
  { fr: "le raisin", ko: "포도", alt: [], topic: "food", pos: "n" },
  { fr: "la pastèque", ko: "수박", alt: [], topic: "food", pos: "n" },
  { fr: "la mandarine", ko: "귤", alt: [], topic: "food", pos: "n" },
  { fr: "la pomme de terre", ko: "감자", alt: [], topic: "food", pos: "n" },
  { fr: "la carotte", ko: "당근", alt: [], topic: "food", pos: "n" },
  { fr: "le concombre", ko: "오이", alt: [], topic: "food", pos: "n" },
  { fr: "le chou chinois", ko: "배추", alt: [], topic: "food", pos: "n" },
  { fr: "la tomate", ko: "토마토", alt: [], topic: "food", pos: "n" },
  { fr: "l'eau", ko: "물", alt: [], topic: "food", pos: "n" },
  { fr: "le thé", ko: "차", alt: [], topic: "food", pos: "n" },
  { fr: "le café (boisson)", ko: "커피", alt: [], topic: "food", pos: "n" },
  { fr: "le lait", ko: "우유", alt: [], topic: "food", pos: "n" },
  { fr: "le jus", ko: "주스", alt: ["쥬스"], topic: "food", pos: "n" },
  { fr: "la bière", ko: "맥주", alt: [], topic: "food", pos: "n" },
  { fr: "le soju", ko: "소주", alt: [], topic: "food", pos: "n" },
  { fr: "l'alcool", ko: "술", alt: [], topic: "food", pos: "n" },
  { fr: "le vin", ko: "와인", alt: ["포도주"], topic: "food", pos: "n" },
  { fr: "le coca", ko: "콜라", alt: [], topic: "food", pos: "n" },
  { fr: "le petit-déjeuner (repas)", ko: "아침", alt: ["아침밥", "아침 식사"], topic: "food", pos: "n" },
  { fr: "le déjeuner (repas)", ko: "점심", alt: ["점심밥", "점심 식사"], topic: "food", pos: "n" },
  { fr: "le dîner (repas)", ko: "저녁", alt: ["저녁밥", "저녁 식사"], topic: "food", pos: "n" },
  { fr: "le restaurant", ko: "식당", alt: ["레스토랑"], topic: "food", pos: "n" },
  { fr: "le café (lieu)", ko: "카페", alt: ["커피숍"], topic: "food", pos: "n" },
  { fr: "le menu", ko: "메뉴", alt: [], topic: "food", pos: "n" },
  { fr: "les baguettes", ko: "젓가락", alt: [], topic: "food", pos: "n" },
  { fr: "la cuillère", ko: "숟가락", alt: [], topic: "food", pos: "n" },
  { fr: "la fourchette", ko: "포크", alt: [], topic: "food", pos: "n" },
  { fr: "la tasse / le verre", ko: "컵", alt: ["잔"], topic: "food", pos: "n" },
  { fr: "le bol", ko: "그릇", alt: [], topic: "food", pos: "n" },
  { fr: "l'assiette", ko: "접시", alt: [], topic: "food", pos: "n" },
  { fr: "le sel", ko: "소금", alt: [], topic: "food", pos: "n" },
  { fr: "le sucre", ko: "설탕", alt: [], topic: "food", pos: "n" },
  { fr: "le piment", ko: "고추", alt: [], topic: "food", pos: "n" },
  { fr: "l'ail", ko: "마늘", alt: [], topic: "food", pos: "n" },
  { fr: "l'oignon", ko: "양파", alt: [], topic: "food", pos: "n" },
  { fr: "l'algue séchée", ko: "김", alt: [], topic: "food", pos: "n" },
  { fr: "le gâteau de riz", ko: "떡", alt: [], topic: "food", pos: "n" },
  { fr: "les biscuits", ko: "과자", alt: ["쿠키", "비스킷"], topic: "food", pos: "n" },
  { fr: "le bonbon", ko: "사탕", alt: [], topic: "food", pos: "n" },
  { fr: "le chocolat", ko: "초콜릿", alt: [], topic: "food", pos: "n" },
  { fr: "la glace (dessert)", ko: "아이스크림", alt: [], topic: "food", pos: "n" },
  { fr: "la pizza", ko: "피자", alt: [], topic: "food", pos: "n" },
  { fr: "le hamburger", ko: "햄버거", alt: [], topic: "food", pos: "n" },
  { fr: "le sandwich", ko: "샌드위치", alt: [], topic: "food", pos: "n" },
  { fr: "le kimbap", ko: "김밥", alt: [], topic: "food", pos: "n" },
  { fr: "le bibimbap", ko: "비빔밥", alt: [], topic: "food", pos: "n" },
  { fr: "le bulgogi", ko: "불고기", alt: [], topic: "food", pos: "n" },
  { fr: "la pâte de soja", ko: "된장", alt: [], topic: "food", pos: "n" },
  { fr: "la pâte de piment", ko: "고추장", alt: [], topic: "food", pos: "n" },
  { fr: "la sauce soja", ko: "간장", alt: [], topic: "food", pos: "n" },
  { fr: "l'huile", ko: "기름", alt: [], topic: "food", pos: "n" },
  { fr: "les nouilles", ko: "국수", alt: ["면"], topic: "food", pos: "n" },
  { fr: "les raviolis", ko: "만두", alt: [], topic: "food", pos: "n" },
  { fr: "la nourriture", ko: "음식", alt: [], topic: "food", pos: "n" },
  { fr: "les accompagnements (plats d'accompagnement)", ko: "반찬", alt: [], topic: "food", pos: "n" },
  { fr: "le dessert", ko: "후식", alt: ["디저트"], topic: "food", pos: "n" },
  { fr: "le gâteau", ko: "케이크", alt: [], topic: "food", pos: "n" },
]; const T_HOME: KoreanEntry[] = [ // home
  { fr: "la maison", ko: "집", alt: [], topic: "home", pos: "n" },
  { fr: "la chambre", ko: "방", alt: [], topic: "home", pos: "n" },
  { fr: "la cuisine (pièce)", ko: "부엌", alt: ["주방"], topic: "home", pos: "n" },
  { fr: "les toilettes", ko: "화장실", alt: [], topic: "home", pos: "n" },
  { fr: "le salon", ko: "거실", alt: [], topic: "home", pos: "n" },
  { fr: "la porte", ko: "문", alt: [], topic: "home", pos: "n" },
  { fr: "la fenêtre", ko: "창문", alt: [], topic: "home", pos: "n" },
  { fr: "le lit", ko: "침대", alt: [], topic: "home", pos: "n" },
  { fr: "le bureau (meuble)", ko: "책상", alt: [], topic: "home", pos: "n" },
  { fr: "la chaise", ko: "의자", alt: [], topic: "home", pos: "n" },
  { fr: "le canapé", ko: "소파", alt: [], topic: "home", pos: "n" },
  { fr: "le réfrigérateur", ko: "냉장고", alt: [], topic: "home", pos: "n" },
  { fr: "la machine à laver", ko: "세탁기", alt: [], topic: "home", pos: "n" },
  { fr: "la télévision", ko: "텔레비전", alt: ["티비", "티브이", "텔레비젼"], topic: "home", pos: "n" },
  { fr: "l'ordinateur", ko: "컴퓨터", alt: [], topic: "home", pos: "n" },
  { fr: "le téléphone", ko: "전화", alt: ["전화기"], topic: "home", pos: "n" },
  { fr: "le téléphone portable", ko: "휴대폰", alt: ["핸드폰", "휴대 전화", "스마트폰"], topic: "home", pos: "n" },
  { fr: "la clé", ko: "열쇠", alt: [], topic: "home", pos: "n" },
  { fr: "la lumière (lampe)", ko: "불", alt: ["전등"], topic: "home", pos: "n" },
  { fr: "le mur", ko: "벽", alt: [], topic: "home", pos: "n" },
  { fr: "le sol (plancher)", ko: "바닥", alt: [], topic: "home", pos: "n" },
  { fr: "le plafond", ko: "천장", alt: [], topic: "home", pos: "n" },
  { fr: "l'escalier", ko: "계단", alt: [], topic: "home", pos: "n" },
  { fr: "l'ascenseur", ko: "엘리베이터", alt: [], topic: "home", pos: "n" },
  { fr: "l'appartement", ko: "아파트", alt: [], topic: "home", pos: "n" },
  { fr: "l'adresse", ko: "주소", alt: [], topic: "home", pos: "n" },
  { fr: "la couette", ko: "이불", alt: [], topic: "home", pos: "n" },
  { fr: "l'oreiller", ko: "베개", alt: [], topic: "home", pos: "n" },
  { fr: "la serviette", ko: "수건", alt: ["타월"], topic: "home", pos: "n" },
  { fr: "le savon", ko: "비누", alt: [], topic: "home", pos: "n" },
  { fr: "la brosse à dents", ko: "칫솔", alt: [], topic: "home", pos: "n" },
  { fr: "le dentifrice", ko: "치약", alt: [], topic: "home", pos: "n" },
  { fr: "le miroir", ko: "거울", alt: [], topic: "home", pos: "n" },
  { fr: "le sac (sac à main, sac à dos)", ko: "가방", alt: [], topic: "home", pos: "n" },
  { fr: "le parapluie", ko: "우산", alt: [], topic: "home", pos: "n" },
  { fr: "la photo", ko: "사진", alt: [], topic: "home", pos: "n" },
  { fr: "le tableau / le dessin (image)", ko: "그림", alt: [], topic: "home", pos: "n" },
  { fr: "la fleur", ko: "꽃", alt: [], topic: "home", pos: "n" },
  { fr: "les ordures", ko: "쓰레기", alt: [], topic: "home", pos: "n" },
  { fr: "le ménage", ko: "청소", alt: [], topic: "home", pos: "n" },
  { fr: "la lessive", ko: "빨래", alt: ["세탁"], topic: "home", pos: "n" },
  { fr: "la cuisine (activité)", ko: "요리", alt: [], topic: "home", pos: "n" },
  { fr: "l'objet", ko: "물건", alt: [], topic: "home", pos: "n" },
  { fr: "la boîte", ko: "상자", alt: ["박스"], topic: "home", pos: "n" },
  { fr: "le papier", ko: "종이", alt: [], topic: "home", pos: "n" },
  { fr: "le stylo", ko: "펜", alt: ["볼펜"], topic: "home", pos: "n" },
  { fr: "le crayon", ko: "연필", alt: [], topic: "home", pos: "n" },
  { fr: "le livre", ko: "책", alt: [], topic: "home", pos: "n" },
  { fr: "l'armoire", ko: "옷장", alt: [], topic: "home", pos: "n" },
  { fr: "la climatisation", ko: "에어컨", alt: [], topic: "home", pos: "n" },
  { fr: "les meubles", ko: "가구", alt: [], topic: "home", pos: "n" },
  { fr: "la poubelle", ko: "쓰레기통", alt: [], topic: "home", pos: "n" },
]; const T_CLOTHES_COLOURS: KoreanEntry[] = [ // clothes-colours
  { fr: "les vêtements", ko: "옷", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "le pantalon", ko: "바지", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "la jupe", ko: "치마", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "la chemise", ko: "셔츠", alt: ["와이셔츠"], topic: "clothes-colours", pos: "n" },
  { fr: "le t-shirt", ko: "티셔츠", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "le manteau", ko: "코트", alt: ["외투"], topic: "clothes-colours", pos: "n" },
  { fr: "les chaussures", ko: "신발", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "les chaussettes", ko: "양말", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "le chapeau", ko: "모자", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "les lunettes", ko: "안경", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "la bague", ko: "반지", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "le collier", ko: "목걸이", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "le portefeuille", ko: "지갑", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "la couleur", ko: "색", alt: ["색깔"], topic: "clothes-colours", pos: "n" },
  { fr: "la taille (dimension)", ko: "크기", alt: ["사이즈"], topic: "clothes-colours", pos: "n" },
  { fr: "rouge", ko: "빨간색", alt: ["빨갛다", "빨강"], topic: "clothes-colours", pos: "n" },
  { fr: "bleu", ko: "파란색", alt: ["파랗다", "파랑"], topic: "clothes-colours", pos: "n" },
  { fr: "jaune", ko: "노란색", alt: ["노랗다", "노랑"], topic: "clothes-colours", pos: "n" },
  { fr: "vert", ko: "초록색", alt: ["녹색", "초록"], topic: "clothes-colours", pos: "n" },
  { fr: "noir", ko: "검은색", alt: ["검정색", "까만색", "검다", "까맣다", "검정"], topic: "clothes-colours", pos: "n" },
  { fr: "blanc", ko: "흰색", alt: ["하얀색", "하얗다", "희다", "하양"], topic: "clothes-colours", pos: "n" },
  { fr: "rose (couleur)", ko: "분홍색", alt: ["핑크색", "분홍", "핑크"], topic: "clothes-colours", pos: "n" },
  { fr: "orange (couleur)", ko: "주황색", alt: ["주황"], topic: "clothes-colours", pos: "n" },
  { fr: "violet", ko: "보라색", alt: ["보라"], topic: "clothes-colours", pos: "n" },
  { fr: "marron", ko: "갈색", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "gris", ko: "회색", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "le jean", ko: "청바지", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "la robe", ko: "원피스", alt: ["드레스"], topic: "clothes-colours", pos: "n" },
  { fr: "la veste", ko: "재킷", alt: ["자켓"], topic: "clothes-colours", pos: "n" },
  { fr: "le pull", ko: "스웨터", alt: ["니트"], topic: "clothes-colours", pos: "n" },
  { fr: "les gants", ko: "장갑", alt: [], topic: "clothes-colours", pos: "n" },
  { fr: "l'écharpe", ko: "목도리", alt: ["스카프"], topic: "clothes-colours", pos: "n" },
  { fr: "les baskets", ko: "운동화", alt: [], topic: "clothes-colours", pos: "n" },
]; const T_CITY_TRANSPORT: KoreanEntry[] = [ // city-transport
  { fr: "la ville", ko: "도시", alt: [], topic: "city-transport", pos: "n" },
  { fr: "la campagne", ko: "시골", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le pays", ko: "나라", alt: ["국가"], topic: "city-transport", pos: "n" },
  { fr: "la Corée", ko: "한국", alt: [], topic: "city-transport", pos: "n" },
  { fr: "la France", ko: "프랑스", alt: [], topic: "city-transport", pos: "n" },
  { fr: "Séoul", ko: "서울", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le chemin (la route)", ko: "길", alt: ["도로"], topic: "city-transport", pos: "n" },
  { fr: "la rue", ko: "거리", alt: ["길"], topic: "city-transport", pos: "n" },
  { fr: "la ruelle", ko: "골목", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le bâtiment", ko: "건물", alt: ["빌딩"], topic: "city-transport", pos: "n" },
  { fr: "le magasin", ko: "가게", alt: ["상점"], topic: "city-transport", pos: "n" },
  { fr: "le marché", ko: "시장", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le grand magasin", ko: "백화점", alt: [], topic: "city-transport", pos: "n" },
  { fr: "la supérette (ouverte 24h)", ko: "편의점", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le supermarché", ko: "슈퍼마켓", alt: ["슈퍼", "마트"], topic: "city-transport", pos: "n" },
  { fr: "la banque", ko: "은행", alt: [], topic: "city-transport", pos: "n" },
  { fr: "la poste (bureau de poste)", ko: "우체국", alt: [], topic: "city-transport", pos: "n" },
  { fr: "l'école", ko: "학교", alt: [], topic: "city-transport", pos: "n" },
  { fr: "l'entreprise (la société)", ko: "회사", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le parc", ko: "공원", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le théâtre", ko: "극장", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le cinéma", ko: "영화관", alt: ["극장"], topic: "city-transport", pos: "n" },
  { fr: "la bibliothèque", ko: "도서관", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le musée", ko: "박물관", alt: [], topic: "city-transport", pos: "n" },
  { fr: "l'église", ko: "교회", alt: [], topic: "city-transport", pos: "n" },
  { fr: "la gare (train, métro)", ko: "역", alt: ["기차역", "지하철역"], topic: "city-transport", pos: "n" },
  { fr: "l'arrêt de bus", ko: "버스 정류장", alt: ["정류장"], topic: "city-transport", pos: "n" },
  { fr: "l'aéroport", ko: "공항", alt: [], topic: "city-transport", pos: "n" },
  { fr: "l'hôtel", ko: "호텔", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le métro", ko: "지하철", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le bus", ko: "버스", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le taxi", ko: "택시", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le train", ko: "기차", alt: ["열차"], topic: "city-transport", pos: "n" },
  { fr: "la voiture", ko: "자동차", alt: ["차"], topic: "city-transport", pos: "n" },
  { fr: "le vélo", ko: "자전거", alt: [], topic: "city-transport", pos: "n" },
  { fr: "l'avion", ko: "비행기", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le bateau", ko: "배", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le billet (transport, spectacle)", ko: "표", alt: ["티켓"], topic: "city-transport", pos: "n" },
  { fr: "la carte (géographique)", ko: "지도", alt: [], topic: "city-transport", pos: "n" },
  { fr: "la gauche", ko: "왼쪽", alt: [], topic: "city-transport", pos: "n" },
  { fr: "la droite", ko: "오른쪽", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le devant (l'avant)", ko: "앞", alt: [], topic: "city-transport", pos: "n" },
  { fr: "l'arrière (derrière)", ko: "뒤", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le haut (au-dessus)", ko: "위", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le bas (en dessous)", ko: "아래", alt: ["밑"], topic: "city-transport", pos: "n" },
  { fr: "le côté (à côté)", ko: "옆", alt: [], topic: "city-transport", pos: "n" },
  { fr: "l'intérieur (dedans)", ko: "안", alt: ["속"], topic: "city-transport", pos: "n" },
  { fr: "l'extérieur (dehors)", ko: "밖", alt: ["바깥"], topic: "city-transport", pos: "n" },
  { fr: "le milieu (le centre)", ko: "가운데", alt: ["중간"], topic: "city-transport", pos: "n" },
  { fr: "les environs (près de)", ko: "근처", alt: ["주변"], topic: "city-transport", pos: "n" },
  { fr: "l'espace entre (entre deux choses)", ko: "사이", alt: [], topic: "city-transport", pos: "n" },
  { fr: "l'est", ko: "동쪽", alt: [], topic: "city-transport", pos: "n" },
  { fr: "l'ouest", ko: "서쪽", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le sud", ko: "남쪽", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le nord", ko: "북쪽", alt: [], topic: "city-transport", pos: "n" },
  { fr: "l'entrée (d'un lieu)", ko: "입구", alt: [], topic: "city-transport", pos: "n" },
  { fr: "la sortie", ko: "출구", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le feu (de circulation)", ko: "신호등", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le passage piéton", ko: "횡단보도", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le parking", ko: "주차장", alt: [], topic: "city-transport", pos: "n" },
  { fr: "le pont (sur une rivière)", ko: "다리", alt: [], topic: "city-transport", pos: "n" },
]; const T_NATURE_WEATHER: KoreanEntry[] = [ // nature-weather
  { fr: "le temps (météo)", ko: "날씨", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la pluie", ko: "비", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la neige", ko: "눈", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le vent", ko: "바람", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le nuage", ko: "구름", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le ciel", ko: "하늘", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le soleil", ko: "해", alt: ["태양"], topic: "nature-weather", pos: "n" },
  { fr: "la lune", ko: "달", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "l'étoile", ko: "별", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la montagne", ko: "산", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la mer", ko: "바다", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le fleuve (la rivière)", ko: "강", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le lac", ko: "호수", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "l'île", ko: "섬", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la forêt", ko: "숲", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "l'arbre", ko: "나무", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "l'herbe", ko: "풀", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la pierre", ko: "돌", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la terre (le sol, la terre meuble)", ko: "흙", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le sol (le terrain, la terre ferme)", ko: "땅", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le monde", ko: "세계", alt: ["세상"], topic: "nature-weather", pos: "n" },
  { fr: "l'air", ko: "공기", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le feu", ko: "불", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la température", ko: "온도", alt: ["기온"], topic: "nature-weather", pos: "n" },
  { fr: "le brouillard", ko: "안개", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "l'arc-en-ciel", ko: "무지개", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le typhon", ko: "태풍", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la saison des pluies", ko: "장마", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "l'animal", ko: "동물", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le chien", ko: "개", alt: ["강아지"], topic: "nature-weather", pos: "n" },
  { fr: "le chat", ko: "고양이", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "l'oiseau", ko: "새", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le poisson (animal vivant)", ko: "물고기", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la vache (le bœuf)", ko: "소", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le cochon", ko: "돼지", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la poule (le coq)", ko: "닭", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le cheval", ko: "말", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la souris (le rat)", ko: "쥐", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le tigre", ko: "호랑이", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "l'ours", ko: "곰", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le lapin", ko: "토끼", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le singe", ko: "원숭이", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "le serpent", ko: "뱀", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "l'insecte", ko: "벌레", alt: ["곤충"], topic: "nature-weather", pos: "n" },
  { fr: "le papillon", ko: "나비", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la nature", ko: "자연", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "l'environnement", ko: "환경", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "la Terre (planète)", ko: "지구", alt: [], topic: "nature-weather", pos: "n" },
  { fr: "l'univers (l'espace)", ko: "우주", alt: [], topic: "nature-weather", pos: "n" },
]; const T_SCHOOL_WORK: KoreanEntry[] = [ // school-work
  { fr: "l'élève (l'étudiant)", ko: "학생", alt: [], topic: "school-work", pos: "n" },
  { fr: "l'étudiant (à l'université)", ko: "대학생", alt: [], topic: "school-work", pos: "n" },
  { fr: "le professeur (l'enseignant)", ko: "선생님", alt: ["선생"], topic: "school-work", pos: "n" },
  { fr: "le professeur d'université", ko: "교수", alt: ["교수님"], topic: "school-work", pos: "n" },
  { fr: "la salle de classe", ko: "교실", alt: [], topic: "school-work", pos: "n" },
  { fr: "le cours (la leçon)", ko: "수업", alt: ["강의"], topic: "school-work", pos: "n" },
  { fr: "les devoirs (à la maison)", ko: "숙제", alt: ["과제"], topic: "school-work", pos: "n" },
  { fr: "l'examen", ko: "시험", alt: [], topic: "school-work", pos: "n" },
  { fr: "la question", ko: "질문", alt: [], topic: "school-work", pos: "n" },
  { fr: "la réponse", ko: "대답", alt: ["답"], topic: "school-work", pos: "n" },
  { fr: "le problème (l'exercice)", ko: "문제", alt: [], topic: "school-work", pos: "n" },
  { fr: "les études (le fait d'étudier)", ko: "공부", alt: [], topic: "school-work", pos: "n" },
  { fr: "la note / le score (points)", ko: "점수", alt: [], topic: "school-work", pos: "n" },
  { fr: "le manuel scolaire", ko: "교과서", alt: [], topic: "school-work", pos: "n" },
  { fr: "le dictionnaire", ko: "사전", alt: [], topic: "school-work", pos: "n" },
  { fr: "le cahier", ko: "공책", alt: ["노트"], topic: "school-work", pos: "n" },
  { fr: "la gomme", ko: "지우개", alt: [], topic: "school-work", pos: "n" },
  { fr: "le tableau (de classe)", ko: "칠판", alt: [], topic: "school-work", pos: "n" },
  { fr: "l'université", ko: "대학교", alt: ["대학"], topic: "school-work", pos: "n" },
  { fr: "le lycée", ko: "고등학교", alt: [], topic: "school-work", pos: "n" },
  { fr: "le collège", ko: "중학교", alt: [], topic: "school-work", pos: "n" },
  { fr: "l'école primaire", ko: "초등학교", alt: [], topic: "school-work", pos: "n" },
  { fr: "l'école maternelle", ko: "유치원", alt: [], topic: "school-work", pos: "n" },
  { fr: "l'année scolaire (le niveau, ex. 1re année)", ko: "학년", alt: [], topic: "school-work", pos: "n" },
  { fr: "la classe (le groupe d'élèves)", ko: "반", alt: [], topic: "school-work", pos: "n" },
  { fr: "la fin des études (la remise des diplômes)", ko: "졸업", alt: [], topic: "school-work", pos: "n" },
  { fr: "l'entrée à l'école (l'admission)", ko: "입학", alt: [], topic: "school-work", pos: "n" },
  { fr: "le coréen (langue)", ko: "한국어", alt: ["한국말"], topic: "school-work", pos: "n" },
  { fr: "l'anglais (langue)", ko: "영어", alt: [], topic: "school-work", pos: "n" },
  { fr: "le français (langue)", ko: "프랑스어", alt: ["불어"], topic: "school-work", pos: "n" },
  { fr: "la langue étrangère", ko: "외국어", alt: [], topic: "school-work", pos: "n" },
  { fr: "le mot (vocabulaire)", ko: "단어", alt: ["낱말"], topic: "school-work", pos: "n" },
  { fr: "la phrase", ko: "문장", alt: [], topic: "school-work", pos: "n" },
  { fr: "la grammaire", ko: "문법", alt: [], topic: "school-work", pos: "n" },
  { fr: "la prononciation", ko: "발음", alt: [], topic: "school-work", pos: "n" },
  { fr: "le sens (la signification)", ko: "뜻", alt: ["의미"], topic: "school-work", pos: "n" },
  { fr: "le nom (le prénom)", ko: "이름", alt: [], topic: "school-work", pos: "n" },
  { fr: "le lieu de travail", ko: "직장", alt: [], topic: "school-work", pos: "n" },
  { fr: "le travail (la tâche, le boulot)", ko: "일", alt: [], topic: "school-work", pos: "n" },
  { fr: "le métier (la profession)", ko: "직업", alt: [], topic: "school-work", pos: "n" },
  { fr: "le petit boulot (le job à temps partiel)", ko: "아르바이트", alt: ["알바"], topic: "school-work", pos: "n" },
  { fr: "le patron (le PDG)", ko: "사장", alt: ["사장님"], topic: "school-work", pos: "n" },
  { fr: "l'employé (le membre du personnel)", ko: "직원", alt: [], topic: "school-work", pos: "n" },
  { fr: "l'employé de bureau (le salarié)", ko: "회사원", alt: [], topic: "school-work", pos: "n" },
  { fr: "la réunion", ko: "회의", alt: [], topic: "school-work", pos: "n" },
  { fr: "le salaire (mensuel)", ko: "월급", alt: [], topic: "school-work", pos: "n" },
  { fr: "le bureau (la pièce de travail)", ko: "사무실", alt: [], topic: "school-work", pos: "n" },
  { fr: "le document (les papiers)", ko: "서류", alt: [], topic: "school-work", pos: "n" },
  { fr: "l'e-mail", ko: "이메일", alt: ["메일"], topic: "school-work", pos: "n" },
  { fr: "le numéro de téléphone", ko: "전화번호", alt: [], topic: "school-work", pos: "n" },
  { fr: "Internet", ko: "인터넷", alt: [], topic: "school-work", pos: "n" },
  { fr: "le policier (la police)", ko: "경찰", alt: ["경찰관"], topic: "school-work", pos: "n" },
  { fr: "le cuisinier", ko: "요리사", alt: [], topic: "school-work", pos: "n" },
  { fr: "le chanteur", ko: "가수", alt: [], topic: "school-work", pos: "n" },
  { fr: "l'acteur", ko: "배우", alt: [], topic: "school-work", pos: "n" },
  { fr: "le sportif (l'athlète)", ko: "운동선수", alt: ["선수"], topic: "school-work", pos: "n" },
  { fr: "le journaliste", ko: "기자", alt: [], topic: "school-work", pos: "n" },
  { fr: "l'avocat (métier)", ko: "변호사", alt: [], topic: "school-work", pos: "n" },
  { fr: "le soldat (le militaire)", ko: "군인", alt: [], topic: "school-work", pos: "n" },
  { fr: "l'agriculteur", ko: "농부", alt: [], topic: "school-work", pos: "n" },
  { fr: "la femme au foyer", ko: "주부", alt: [], topic: "school-work", pos: "n" },
]; const T_SHOPPING_MONEY: KoreanEntry[] = [ // shopping-money
  { fr: "l'argent", ko: "돈", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "le prix", ko: "가격", alt: ["값"], topic: "shopping-money", pos: "n" },
  { fr: "la réduction (la remise)", ko: "할인", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "les soldes (la promotion)", ko: "세일", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "l'argent liquide (les espèces)", ko: "현금", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "la carte (bancaire)", ko: "카드", alt: ["신용카드"], topic: "shopping-money", pos: "n" },
  { fr: "le reçu (le ticket de caisse)", ko: "영수증", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "le paiement (le calcul à la caisse)", ko: "계산", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "le propriétaire (le patron d'une boutique)", ko: "주인", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "le vendeur (l'employé de magasin)", ko: "점원", alt: ["판매원"], topic: "shopping-money", pos: "n" },
  { fr: "le cadeau", ko: "선물", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "le shopping (les courses)", ko: "쇼핑", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "la gratuité (gratuit, sans frais)", ko: "무료", alt: ["공짜"], topic: "shopping-money", pos: "n" },
  { fr: "le billet (de banque)", ko: "지폐", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "la pièce (de monnaie)", ko: "동전", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "la monnaie (rendue)", ko: "거스름돈", alt: ["잔돈"], topic: "shopping-money", pos: "n" },
  { fr: "le sac (en plastique ou papier, l'enveloppe)", ko: "봉투", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "le nombre (la quantité d'articles)", ko: "개수", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "le produit (la marchandise)", ko: "상품", alt: ["제품"], topic: "shopping-money", pos: "n" },
  { fr: "la commande", ko: "주문", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "la livraison", ko: "배달", alt: ["배송"], topic: "shopping-money", pos: "n" },
  { fr: "l'échange (d'un article)", ko: "교환", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "le remboursement", ko: "환불", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "l'emballage", ko: "포장", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "la caisse (du magasin)", ko: "계산대", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "le compte (bancaire)", ko: "계좌", alt: [], topic: "shopping-money", pos: "n" },
  { fr: "l'impôt (la taxe)", ko: "세금", alt: [], topic: "shopping-money", pos: "n" },
]; const T_VERBS_BASIC: KoreanEntry[] = [ // verbs-basic
  { fr: "faire (verbe général)", ko: "하다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "devenir", ko: "되다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "avoir / il y a (exister)", ko: "있다", alt: ["계시다"], topic: "verbs-basic", pos: "v" },
  { fr: "ne pas avoir / il n'y a pas", ko: "없다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "aller", ko: "가다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "venir", ko: "오다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "voir / regarder", ko: "보다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "manger", ko: "먹다", alt: ["드시다"], topic: "verbs-basic", pos: "v" },
  { fr: "boire", ko: "마시다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "dormir", ko: "자다", alt: ["주무시다"], topic: "verbs-basic", pos: "v" },
  { fr: "donner", ko: "주다", alt: ["드리다"], topic: "verbs-basic", pos: "v" },
  { fr: "recevoir", ko: "받다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "acheter", ko: "사다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "vendre", ko: "팔다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "ouvrir", ko: "열다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "fermer", ko: "닫다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "commencer", ko: "시작하다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "se terminer (finir, intransitif)", ko: "끝나다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "terminer (finir quelque chose)", ko: "끝내다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "achever (un travail, un cours)", ko: "마치다", alt: ["끝내다"], topic: "verbs-basic", pos: "v" },
  { fr: "attendre", ko: "기다리다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "chercher / trouver", ko: "찾다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "perdre (un objet)", ko: "잃어버리다", alt: ["잃다"], topic: "verbs-basic", pos: "v" },
  { fr: "oublier", ko: "잊어버리다", alt: ["잊다"], topic: "verbs-basic", pos: "v" },
  { fr: "demander (poser une question)", ko: "물어보다", alt: ["묻다", "질문하다"], topic: "verbs-basic", pos: "v" },
  { fr: "répondre", ko: "대답하다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "aider", ko: "도와주다", alt: ["돕다"], topic: "verbs-basic", pos: "v" },
  { fr: "appeler (quelqu'un par son nom, héler)", ko: "부르다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "vivre / habiter", ko: "살다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "mourir", ko: "죽다", alt: ["돌아가시다"], topic: "verbs-basic", pos: "v" },
  { fr: "naître", ko: "태어나다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "utiliser", ko: "사용하다", alt: ["쓰다", "이용하다"], topic: "verbs-basic", pos: "v" },
  { fr: "écrire", ko: "쓰다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "lire", ko: "읽다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "écouter / entendre", ko: "듣다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "parler / dire", ko: "말하다", alt: ["이야기하다", "말씀하다"], topic: "verbs-basic", pos: "v" },
  { fr: "mettre (poser)", ko: "놓다", alt: ["두다"], topic: "verbs-basic", pos: "v" },
  { fr: "mettre (dans)", ko: "넣다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "sortir (retirer de)", ko: "꺼내다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "tenir / porter (dans la main)", ko: "들다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "jeter (à la poubelle)", ko: "버리다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "changer (remplacer, échanger quelque chose)", ko: "바꾸다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "choisir", ko: "고르다", alt: ["선택하다"], topic: "verbs-basic", pos: "v" },
  { fr: "préparer", ko: "준비하다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "compter", ko: "세다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "couper", ko: "자르다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "coller", ko: "붙이다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "pousser", ko: "밀다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "tirer (vers soi)", ko: "당기다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "lancer", ko: "던지다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "toucher", ko: "만지다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "attraper / saisir", ko: "잡다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "s'ouvrir", ko: "열리다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "se fermer", ko: "닫히다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "prendre (du temps) / attraper (une maladie)", ko: "걸리다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "passer (le temps, devant un lieu)", ko: "지나다", alt: ["지나가다"], topic: "verbs-basic", pos: "v" },
  { fr: "être en retard", ko: "늦다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "obtenir", ko: "얻다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "rater / manquer", ko: "놓치다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "rester (ce qui reste, en surplus)", ko: "남다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "apparaître / se produire", ko: "생기다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "annuler", ko: "취소하다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "appuyer (sur un bouton)", ko: "누르다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "envoyer / passer (du temps)", ko: "보내다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "continuer", ko: "계속하다", alt: [], topic: "verbs-basic", pos: "v" },
  { fr: "arrêter (abandonner)", ko: "그만두다", alt: ["그만하다"], topic: "verbs-basic", pos: "v" },
]; const T_VERBS_DAILY: KoreanEntry[] = [ // verbs-daily
  { fr: "prendre (une photo)", ko: "찍다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "payer (l'addition) / calculer", ko: "계산하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "allumer (la lumière, un appareil)", ko: "켜다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "éteindre", ko: "끄다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "livrer", ko: "배달하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "aller au travail", ko: "출근하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "finir le travail / rentrer du travail", ko: "퇴근하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "étudier", ko: "공부하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "travailler", ko: "일하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "se reposer", ko: "쉬다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "se laver", ko: "씻다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "prendre une douche", ko: "샤워하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "porter (un vêtement)", ko: "입다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "enlever (un vêtement)", ko: "벗다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "porter (des chaussures)", ko: "신다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "porter (des lunettes, des gants, une bague)", ko: "끼다", alt: ["쓰다"], topic: "verbs-daily", pos: "v" },
  { fr: "fabriquer / faire (créer quelque chose)", ko: "만들다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "cuisiner", ko: "요리하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "faire bouillir", ko: "끓이다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "griller / cuire au four", ko: "굽다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "faire le ménage", ko: "청소하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "faire la lessive", ko: "빨래하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "faire la vaisselle", ko: "설거지하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "essuyer / se brosser (les dents)", ko: "닦다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "faire du sport", ko: "운동하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "passer (un appel) / accrocher", ko: "걸다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "s'entraîner / pratiquer", ko: "연습하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "chanter", ko: "노래하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "danser", ko: "춤추다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "dessiner", ko: "그리다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "jouer (s'amuser)", ko: "놀다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "rire", ko: "웃다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "pleurer", ko: "울다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "fumer", ko: "피우다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "guérir (se rétablir)", ko: "낫다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "se blesser", ko: "다치다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "élever (un enfant, un animal)", ko: "키우다", alt: ["기르다"], topic: "verbs-daily", pos: "v" },
  { fr: "grandir", ko: "자라다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "planter", ko: "심다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "s'occuper de", ko: "돌보다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "ranger", ko: "정리하다", alt: ["치우다"], topic: "verbs-daily", pos: "v" },
  { fr: "vérifier", ko: "확인하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "faire une demande / s'inscrire", ko: "신청하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "réserver", ko: "예약하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "commander (au restaurant)", ko: "주문하다", alt: ["시키다"], topic: "verbs-daily", pos: "v" },
  { fr: "emballer", ko: "포장하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "gagner (de l'argent)", ko: "벌다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "faire les courses / du shopping", ko: "쇼핑하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "visiter (regarder, faire un tour)", ko: "구경하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "profiter de / apprécier", ko: "즐기다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "obtenir son diplôme", ko: "졸업하다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "aller (se porter) / passer (les jours)", ko: "지내다", alt: [], topic: "verbs-daily", pos: "v" },
  { fr: "se maquiller", ko: "화장하다", alt: [], topic: "verbs-daily", pos: "v" },
]; const T_VERBS_MOVE: KoreanEntry[] = [ // verbs-move
  { fr: "se souvenir", ko: "기억하다", alt: ["기억나다"], topic: "verbs-move", pos: "v" },
  { fr: "prendre (un moyen de transport)", ko: "타다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "descendre (d'un véhicule)", ko: "내리다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "décider", ko: "정하다", alt: ["결정하다"], topic: "verbs-move", pos: "v" },
  { fr: "tomber (chuter)", ko: "떨어지다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "tomber (trébucher)", ko: "넘어지다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "partir (départ)", ko: "출발하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "partir / quitter (un lieu)", ko: "떠나다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "arriver", ko: "도착하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "se lever", ko: "일어나다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "s'asseoir", ko: "앉다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "se tenir debout / s'arrêter", ko: "서다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "s'allonger", ko: "눕다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "marcher", ko: "걷다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "courir", ko: "뛰다", alt: ["달리다"], topic: "verbs-move", pos: "v" },
  { fr: "entrer", ko: "들어가다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "entrer (vers celui qui parle)", ko: "들어오다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "sortir", ko: "나가다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "sortir (vers celui qui parle)", ko: "나오다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "retourner (repartir)", ko: "돌아가다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "revenir", ko: "돌아오다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "monter", ko: "올라가다", alt: ["올라오다"], topic: "verbs-move", pos: "v" },
  { fr: "monter / augmenter (prix)", ko: "오르다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "descendre", ko: "내려가다", alt: ["내려오다"], topic: "verbs-move", pos: "v" },
  { fr: "traverser", ko: "건너다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "déménager", ko: "이사하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "voyager", ko: "여행하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "conduire", ko: "운전하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "bouger", ko: "움직이다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "s'arrêter / arrêter", ko: "멈추다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "se dépêcher", ko: "서두르다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "suivre", ko: "따라가다", alt: ["따르다"], topic: "verbs-move", pos: "v" },
  { fr: "tourner", ko: "돌다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "se promener", ko: "산책하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "emporter", ko: "가져가다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "apporter", ko: "가져오다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "changer (de transport)", ko: "갈아타다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "apprendre", ko: "배우다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "enseigner", ko: "가르치다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "savoir / connaître", ko: "알다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "ne pas savoir", ko: "모르다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "penser", ko: "생각하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "aimer (apprécier)", ko: "좋아하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "détester", ko: "싫어하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "aimer (d'amour)", ko: "사랑하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "vouloir (désirer quelque chose)", ko: "원하다", alt: ["싶다"], topic: "verbs-move", pos: "v" },
  { fr: "avoir besoin de / nécessaire", ko: "필요하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "comprendre", ko: "이해하다", alt: ["알아듣다"], topic: "verbs-move", pos: "v" },
  { fr: "croire", ko: "믿다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "ressentir", ko: "느끼다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "s'inquiéter", ko: "걱정하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "faire attention", ko: "조심하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "supporter / patienter", ko: "참다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "faire des efforts", ko: "노력하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "réussir", ko: "성공하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "échouer", ko: "실패하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "gagner (un match)", ko: "이기다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "perdre (un match)", ko: "지다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "se mettre en colère", ko: "화나다", alt: ["화내다"], topic: "verbs-move", pos: "v" },
  { fr: "espérer / souhaiter", ko: "바라다", alt: ["희망하다"], topic: "verbs-move", pos: "v" },
  { fr: "se tromper / avoir faux (incorrect)", ko: "틀리다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "être surpris", ko: "놀라다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "regretter", ko: "후회하다", alt: [], topic: "verbs-move", pos: "v" },
  { fr: "faire savoir / informer", ko: "알려주다", alt: ["알리다"], topic: "verbs-move", pos: "v" },
  { fr: "s'ennuyer (ne rien avoir à faire)", ko: "심심하다", alt: [], topic: "verbs-move", pos: "v" },
]; const T_VERBS_MIND: KoreanEntry[] = [ // verbs-mind
]; const T_VERBS_SOCIAL: KoreanEntry[] = [ // verbs-social
  { fr: "discuter / raconter", ko: "이야기하다", alt: ["말하다"], topic: "verbs-social", pos: "v" },
  { fr: "rembourser / rendre (de l'argent)", ko: "갚다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "emprunter", ko: "빌리다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "prêter", ko: "빌려주다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "rendre visite", ko: "방문하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "téléphoner", ko: "전화하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "rencontrer", ko: "만나다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "se marier", ko: "결혼하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "féliciter", ko: "축하하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "promettre", ko: "약속하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "inviter", ko: "초대하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "présenter", ko: "소개하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "expliquer", ko: "설명하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "se disputer / se battre", ko: "싸우다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "être désolé", ko: "미안하다", alt: ["죄송하다"], topic: "verbs-social", pos: "v" },
  { fr: "s'excuser", ko: "사과하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "remercier", ko: "감사하다", alt: ["고맙다"], topic: "verbs-social", pos: "v" },
  { fr: "saluer", ko: "인사하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "protéger / tenir (une promesse)", ko: "지키다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "bloquer / empêcher", ko: "막다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "partager / diviser", ko: "나누다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "rassembler / collectionner", ko: "모으다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "sortir avec / se lier d'amitié", ko: "사귀다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "se séparer / rompre", ko: "헤어지다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "montrer", ko: "보여주다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "offrir (un cadeau)", ko: "선물하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "se réunir", ko: "모이다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "contacter", ko: "연락하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "mentir", ko: "거짓말하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "demander (un service)", ko: "부탁하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "pardonner", ko: "용서하다", alt: [], topic: "verbs-social", pos: "v" },
  { fr: "participer", ko: "참가하다", alt: ["참석하다", "참여하다"], topic: "verbs-social", pos: "v" },
]; const T_ADJECTIVES: KoreanEntry[] = [ // adjectives
  { fr: "grand (dimension)", ko: "크다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "petit (dimension)", ko: "작다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "nombreux (il y en a beaucoup)", ko: "많다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "peu nombreux (il y en a peu)", ko: "적다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "bon (bien, de bonne qualité)", ko: "좋다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "mauvais", ko: "나쁘다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "joli", ko: "예쁘다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "beau (paysage, magnifique)", ko: "아름답다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "beau, cool (personne, style)", ko: "멋있다", alt: ["멋지다"], topic: "adjectives", pos: "adj" },
  { fr: "mignon", ko: "귀엽다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "laid (personne)", ko: "못생기다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "nouveau", ko: "새롭다", alt: ["새"], topic: "adjectives", pos: "adj" },
  { fr: "vieux, ancien (qui date)", ko: "오래되다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "usé, vieux (objet abîmé)", ko: "낡다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "jeune", ko: "젊다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "vieux (personne âgée)", ko: "늙다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "jeune, petit (enfant en bas âge)", ko: "어리다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "haut", ko: "높다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "bas", ko: "낮다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "long", ko: "길다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "court", ko: "짧다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "large, vaste", ko: "넓다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "étroit", ko: "좁다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "épais", ko: "두껍다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "fin, mince (objet)", ko: "얇다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "lourd", ko: "무겁다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "léger", ko: "가볍다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "rapide", ko: "빠르다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "lent", ko: "느리다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "chaud (objet, boisson)", ko: "뜨겁다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "froid (objet)", ko: "차갑다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "chaud (temps)", ko: "덥다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "froid (temps)", ko: "춥다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "chaud, tiède (agréablement)", ko: "따뜻하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "frais (agréablement)", ko: "시원하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "clair, lumineux", ko: "밝다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "sombre", ko: "어둡다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "facile", ko: "쉽다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "difficile", ko: "어렵다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "cher (prix)", ko: "비싸다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "bon marché", ko: "싸다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "délicieux", ko: "맛있다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "pas bon (goût)", ko: "맛없다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "sucré", ko: "달다", alt: ["달콤하다"], topic: "adjectives", pos: "adj" },
  { fr: "salé", ko: "짜다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "épicé, piquant", ko: "맵다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "amer", ko: "쓰다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "acide", ko: "시다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "fade", ko: "싱겁다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "affamé (avoir faim)", ko: "배고프다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "rassasié (avoir le ventre plein)", ko: "배부르다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "assoiffé (avoir soif)", ko: "목마르다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "malade, douloureux (avoir mal)", ko: "아프다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "fatigué", ko: "피곤하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "occupé", ko: "바쁘다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "libre (avoir du temps)", ko: "한가하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "amusant, intéressant", ko: "재미있다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "ennuyeux (pas intéressant)", ko: "재미없다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "agréable, plaisant", ko: "즐겁다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "content", ko: "기쁘다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "triste", ko: "슬프다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "heureux", ko: "행복하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "effrayant (avoir peur)", ko: "무섭다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "étonnant, surprenant", ko: "놀랍다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "gêné, honteux (avoir honte)", ko: "부끄럽다", alt: ["창피하다"], topic: "adjectives", pos: "adj" },
  { fr: "seul (se sentir seul)", ko: "외롭다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "confortable, pratique", ko: "편하다", alt: ["편리하다"], topic: "adjectives", pos: "adj" },
  { fr: "inconfortable, pas pratique", ko: "불편하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "calme, silencieux", ko: "조용하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "bruyant", ko: "시끄럽다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "propre", ko: "깨끗하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "sale", ko: "더럽다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "proche (distance)", ko: "가깝다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "loin", ko: "멀다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "pareil, même", ko: "같다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "différent", ko: "다르다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "semblable, similaire", ko: "비슷하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "important", ko: "중요하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "célèbre", ko: "유명하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "gentil, aimable (serviable)", ko: "친절하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "intelligent", ko: "똑똑하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "gentil (bon cœur)", ko: "착하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "en bonne santé", ko: "건강하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "solide, robuste", ko: "튼튼하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "dangereux", ko: "위험하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "sûr (en sécurité)", ko: "안전하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "compliqué", ko: "복잡하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "simple", ko: "간단하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "spécial", ko: "특별하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "bizarre, étrange", ko: "이상하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "correct, juste (exact)", ko: "맞다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "pas mal (ça va, c'est bon)", ko: "괜찮다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "suffisant", ko: "충분하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "possible", ko: "가능하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "insuffisant (qui manque)", ko: "부족하다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "exactement pareil, identique", ko: "똑같다", alt: [], topic: "adjectives", pos: "adj" },
  { fr: "fort (puissant)", ko: "강하다", alt: ["세다"], topic: "adjectives", pos: "adj" },
  { fr: "faible", ko: "약하다", alt: [], topic: "adjectives", pos: "adj" },
]; const T_ADVERBS: KoreanEntry[] = [ // adverbs
  { fr: "très", ko: "아주", alt: ["매우"], topic: "adverbs", pos: "adv" },
  { fr: "trop", ko: "너무", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "vraiment", ko: "정말", alt: ["진짜"], topic: "adverbs", pos: "adv" },
  { fr: "un peu", ko: "조금", alt: ["좀", "약간"], topic: "adverbs", pos: "adv" },
  { fr: "beaucoup (adverbe)", ko: "많이", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "plus (davantage)", ko: "더", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "moins", ko: "덜", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "le plus (superlatif)", ko: "가장", alt: ["제일"], topic: "adverbs", pos: "adv" },
  { fr: "bien (adverbe)", ko: "잘", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "ne pas pouvoir (adverbe d'incapacité)", ko: "못", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "ne... pas (adverbe de négation)", ko: "안", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "vite", ko: "빨리", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "lentement", ko: "천천히", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "d'habitude, normalement", ko: "보통", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "pas du tout", ko: "전혀", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "à nouveau, de nouveau", ko: "다시", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "encore (de plus, aussi)", ko: "또", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "ensemble", ko: "같이", alt: ["함께"], topic: "adverbs", pos: "adv" },
  { fr: "tout seul (sans personne)", ko: "혼자", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "avec ardeur (travailler dur)", ko: "열심히", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "soudain, tout à coup", ko: "갑자기", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "peut-être", ko: "아마", alt: ["아마도"], topic: "adverbs", pos: "adv" },
  { fr: "absolument, sans faute", ko: "꼭", alt: ["반드시"], topic: "adverbs", pos: "adv" },
  { fr: "surtout, particulièrement", ko: "특히", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "juste, simplement (comme ça)", ko: "그냥", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "continuellement, sans arrêt", ko: "계속", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "tout de suite, directement", ko: "바로", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "très vite (en un rien de temps)", ko: "금방", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "longtemps", ko: "오래", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "un instant, un moment", ko: "잠깐", alt: ["잠시"], topic: "adverbs", pos: "adv" },
  { fr: "presque", ko: "거의", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "tout (entièrement)", ko: "다", alt: ["전부", "모두"], topic: "adverbs", pos: "adv" },
  { fr: "pas vraiment, pas tellement", ko: "별로", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "donc, alors", ko: "그래서", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "mais, au fait (changement de sujet)", ko: "그런데", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "mais, cependant", ko: "하지만", alt: ["그렇지만", "그러나"], topic: "adverbs", pos: "adv" },
  { fr: "et (entre deux phrases)", ko: "그리고", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "alors, dans ce cas", ko: "그러면", alt: ["그럼"], topic: "adverbs", pos: "adv" },
  { fr: "c'est pour ça, donc (conclusion)", ko: "그러니까", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "parce que", ko: "왜냐하면", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "ou (entre deux phrases)", ko: "또는", alt: ["아니면"], topic: "adverbs", pos: "adv" },
  { fr: "quand même, malgré tout", ko: "그래도", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "par exemple", ko: "예를 들면", alt: ["예를 들어"], topic: "adverbs", pos: "adv" },
  { fr: "en fait", ko: "사실", alt: ["사실은"], topic: "adverbs", pos: "adv" },
  { fr: "bien sûr", ko: "물론", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "comme prévu, effectivement", ko: "역시", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "par hasard (question polie)", ko: "혹시", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "si (hypothèse)", ko: "만약", alt: ["만약에"], topic: "adverbs", pos: "adv" },
  { fr: "l'un l'autre, mutuellement", ko: "서로", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "par soi-même", ko: "스스로", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "à la place, au lieu de", ko: "대신", alt: ["대신에"], topic: "adverbs", pos: "adv" },
  { fr: "beaucoup plus (comparaison)", ko: "훨씬", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "complètement", ko: "완전히", alt: [], topic: "adverbs", pos: "adv" },
  { fr: "enfin (finalement)", ko: "드디어", alt: ["마침내"], topic: "adverbs", pos: "adv" },
  { fr: "tout à l'heure (avant)", ko: "아까", alt: [], topic: "adverbs", pos: "adv" },
]; const T_HOBBIES_MEDIA: KoreanEntry[] = [ // hobbies-media
  { fr: "le passe-temps (hobby)", ko: "취미", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le football", ko: "축구", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le baseball", ko: "야구", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le basket-ball", ko: "농구", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la natation", ko: "수영", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la randonnée (en montagne)", ko: "등산", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la promenade", ko: "산책", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le voyage", ko: "여행", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la musique", ko: "음악", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la chanson", ko: "노래", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la danse", ko: "춤", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le film", ko: "영화", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la série télé", ko: "드라마", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le roman", ko: "소설", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la bande dessinée (manga)", ko: "만화", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le jeu vidéo", ko: "게임", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "les informations (journal télévisé)", ko: "뉴스", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le journal (papier)", ko: "신문", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le magazine", ko: "잡지", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la radio", ko: "라디오", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le concert", ko: "콘서트", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le spectacle", ko: "공연", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le festival", ko: "축제", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la fête (soirée)", ko: "파티", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le match", ko: "경기", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "l'équipe", ko: "팀", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le joueur (sportif)", ko: "선수", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le piano", ko: "피아노", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la guitare", ko: "기타", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "la pêche (loisir)", ko: "낚시", alt: [], topic: "hobbies-media", pos: "n" },
  { fr: "le yoga", ko: "요가", alt: [], topic: "hobbies-media", pos: "n" },
]; const T_TECH: KoreanEntry[] = [ // tech
  { fr: "l'ordinateur portable", ko: "노트북", alt: [], topic: "tech", pos: "n" },
  { fr: "le SMS (texto)", ko: "문자", alt: [], topic: "tech", pos: "n" },
  { fr: "le message", ko: "메시지", alt: [], topic: "tech", pos: "n" },
  { fr: "l'application (appli)", ko: "앱", alt: ["어플"], topic: "tech", pos: "n" },
  { fr: "le site web", ko: "사이트", alt: ["홈페이지", "웹사이트"], topic: "tech", pos: "n" },
  { fr: "le mot de passe", ko: "비밀번호", alt: [], topic: "tech", pos: "n" },
  { fr: "l'écran", ko: "화면", alt: [], topic: "tech", pos: "n" },
  { fr: "le son", ko: "소리", alt: [], topic: "tech", pos: "n" },
  { fr: "la batterie", ko: "배터리", alt: [], topic: "tech", pos: "n" },
  { fr: "le chargeur", ko: "충전기", alt: [], topic: "tech", pos: "n" },
  { fr: "l'appareil photo (caméra)", ko: "카메라", alt: [], topic: "tech", pos: "n" },
  { fr: "le programme (logiciel)", ko: "프로그램", alt: [], topic: "tech", pos: "n" },
  { fr: "le fichier", ko: "파일", alt: [], topic: "tech", pos: "n" },
  { fr: "le téléchargement", ko: "다운로드", alt: [], topic: "tech", pos: "n" },
];

export const KOREAN_CORE: KoreanEntry[] = [
  ...T_GREETINGS,
  ...T_PRONOUNS,
  ...T_NUMBERS,
  ...T_TIME,
  ...T_FAMILY,
  ...T_BODY_HEALTH,
  ...T_FOOD,
  ...T_HOME,
  ...T_CLOTHES_COLOURS,
  ...T_CITY_TRANSPORT,
  ...T_NATURE_WEATHER,
  ...T_SCHOOL_WORK,
  ...T_SHOPPING_MONEY,
  ...T_VERBS_BASIC,
  ...T_VERBS_DAILY,
  ...T_VERBS_MOVE,
  ...T_VERBS_MIND,
  ...T_VERBS_SOCIAL,
  ...T_ADJECTIVES,
  ...T_ADVERBS,
  ...T_HOBBIES_MEDIA,
  ...T_TECH,
];
